// forestvol: the far canopy VOLUME draw (gs-forestvol; APP_FORESTVOL §4a, §4c,
// §5). The FSH1 forest shell of the tiles in range (forestmarch shellPlan:
// one level per 1024 m tile, skirts toward finer neighbours), merged into ONE
// mesh per SHELL_GROUP_M group, all sharing ONE ShaderMaterial whose fragment
// marches the view ray from the shell entry point through the CanopyGen
// tile's max-mip height chain (canopygen.js canopyTextures H). The GLSL ports
// forestmarch.js marchRef / terminalRef / phoneLidH line for line — change
// both or neither. A miss DISCARDS (serrated skylines, real gaps); a hit
// shades from the canopy tile: A (lit colour / W, × meta.tex.light.white),
// look.canopy {gain, sat}, the snow of season.js snowWeight on B (sky-vis,
// sun-vis, snow-catch) lit by the bake's model (canopygen's E on an upward
// normal) × look.snow.gain; then the ONE display curve (tonemapping +
// colorspace) and the scene fog at the hit (terrain's FogExp2).
//
//   stream    re-plan when the eye moved REPLAN_M or the live view changed
//             (vol / shell / sprites / look / season / class); fetch missing
//             `<dir>/<i>_<j>.fsh` (bulk, nearest first, FETCH_MAX in flight),
//             decoded into an LRU (LRU_K × the plan); a tile keeps drawing its
//             previous level until the new level's file is decoded
//   geometry  per group: its drawn tiles + skirts (lid border edges extruded
//             to y0 − SKIRT_DROP_M, facing the finer side), Float32 game
//             metres, aInfo = flag + 4 × level index; ONE group rebuilt at a
//             time — the decode and the group's arrays + cell order on the
//             worker pool (pool.js op forest.shell, pool-forest.js), the main
//             thread wraps them (the same code here when the pool fails)
//   find      an RGBA32F indirection, one texel per 512 m cell over the
//             world (meta.bbox), = (layer, c, y0, 1) of the FINEST resident
//             canopy tile; rebuilt when the resident set changes (gen stats,
//             the pool) — checked per draw, so a slot evicted in genFrame
//             never draws through a stale layer
//   march     uSteps per camera (onBeforeRender: the optic cameras take
//             steps_optic); 'floor' / 'below' / 'up' discard; a tile exit
//             re-looks-up the indirection and goes on in the next tile at
//             MARCH_L0 (the reference: 'exit' → the
//             next tile's marchRef; the steps are shared across tiles);
//             out of steps: a hit only inside the canopy envelope (a
//             falling ray h ≤ H[L], a rising one h ≤ H[min(L, FV_L0)];
//             > 0), else a miss ('steps-empty' / 'steps-above')
//   shade     A / B fetched nearest at the hit's own cell on the footprint
//             level (no filter blend with the empty cells' rgb 0)
//   steps 0   (phones) no march: terminalRef at the entry, the lid / wall
//             tops sunk to the canopy in the VS (phoneLidH, ruling 3)
//   cut       the main pass (viscull.js `pass`: its conservative depth,
//             zCover per cell, a texture per key): past the bound + CUT_Q
//             every later hit fails the depth test at every sample (or lies
//             past the range) — the march discards (forestmarch THE DEPTH
//             CUT); the optic passes march uncut
//   depth     (depthMode) desktop + Quest: gl_FragDepth at the hit
//             (FRAG_DEPTH); phones (steps 0): the hit IS the drawn (sunk)
//             shell, its own depth
//   hand-off  a hit nearer than sprites.view_m − fade_m discards; over fade_m
//             a screen-door dither (interleaved gradient noise of the pixel)
//             fades it in, so the volume never doubles the near sprites
// The shell's triangles face out (+y lid, walls away from the forest, CCW in
// three's right-handed x / y / z): FrontSide draws the outside. A camera
// INSIDE the envelope (under the crowns) does not see the inner faces of its
// own part of the shell — the near sprites own that range.
import * as THREE from 'three';
import { fetchAsset } from './assetq.js';
import { run } from './pool.js';
import {
  SHELL_LEVELS, SHELL_GROUP_M, TOP_PAD_M, MARCH_L0, MARCH_LMAX, MARCH_EPS, MARCH_MAX,
  PHONE_COVER, SHADE_COV, SHADE_UP, ROUND_DROP, ROUND_NS, ROUND_BIS, ROUND_KA, ROUND_K, ROUND_RIM, shellRings, shellIndex, shellPlan, stepsFor, phoneLevel, depthMode,
  CUT_Q, CUT_PAD_M, CUT_EYE_M, SEAT_MAX_M, seatSink,
} from './forestmarch.js';
import { CANOPY_N, H_Q, CANOPY_LEVELS, levelTexel, levelTile, hexLin } from './crowns.js';
import { canopyTextures, canopyTiles } from './canopygen.js';
import { gen, genLive, onGenChange } from './gensched.js';
import { deviceClass } from './device.js';
import { SNOW_GLSL, SEASON, applySeason, readSea } from './season.js';
import { sunDirFromLighting } from './scenelight.js';
import { VEG_LUMA } from './vegpaint.js';
import { famScene } from './famtris.js';
import { HANDOFF, HANDOFF_GLSL, HO_PUSH_M, setHandoffView } from './handoff.js';

const IND_M = levelTile(0);         // the indirection cell: a level-0 canopy tile (512 m)
const REPLAN_M = 128;               // the eye moved this far: a new shell plan
const LRU_K = 3;                    // decoded shell tiles kept: LRU_K × the plan (≥ LRU_MIN)
const LRU_MIN = 16;
const FETCH_MAX = 6;                // .fsh fetches in flight (nearest first)
const RETRY_MS = 5000;              // a failed / dropped fetch is asked again after
const SEAT_MS = 1;                  // THE SEAT: the wall-foot re-read's main-thread ms a tick
const SNOW_ALB = hexLin('#f2f5f8'); // the far snow's albedo (linear; sky.js cSnow)
const _v2 = new THREE.Vector2();
export const SPLAT_TILE_M = 1024;   // the splats' cover grid (= the crown pack tile, VEG_TILE_M)
let _covNone = null;
function splatCovNone() {
  if (_covNone) return _covNone;
  _covNone = new THREE.DataTexture(new Uint8Array(1), 1, 1, THREE.RedFormat, THREE.UnsignedByteType);
  _covNone.minFilter = _covNone.magFilter = THREE.NearestFilter;
  _covNone.generateMipmaps = false;
  _covNone.needsUpdate = true;
  return _covNone;
}

// ---------------------------------------------------------------- GLSL
const f1 = (v) => (Number.isInteger(v) ? v.toFixed(1) : String(v));
const v3 = (a) => `vec3(${a.map((x) => x.toFixed(5)).join(', ')})`;
// phoneLevel(SHELL_LEVELS[lv], texel of canopy level c) at lv × CANOPY_LEVELS + c
const PL = SHELL_LEVELS.flatMap((cm) => Array.from({ length: CANOPY_LEVELS }, (_, c) => phoneLevel(cm, levelTexel(c))));

// the canopy lookup (the volume, and the near sprites' light: trees.js):
// the finest resident canopy tile over a 512 m world cell
export const CANOPY_LOOKUP_GLSL = `
#define FV_IND ${f1(IND_M)}
uniform sampler2D uInd;             // per 512 m cell: layer, c, y0, valid
uniform vec2 uIndOrigin, uIndN;     // the indirection's origin cell, cells (x, z)
// the finest resident canopy tile over world cell q -> T (layer, c, its
// origin cell x, z), y0 (m); false: none
bool tileOf(ivec2 q, out ivec4 T, out float y0) {
  ivec2 r = q - ivec2(uIndOrigin);
  if (r.x < 0 || r.y < 0 || r.x >= int(uIndN.x) || r.y >= int(uIndN.y)) return false;
  vec4 e = texelFetch(uInd, r, 0);
  if (e.w < 0.5) return false;
  int c = int(e.y + 0.5);
  T = ivec4(int(e.x + 0.5), c, ((r >> c) << c) + ivec2(uIndOrigin));
  y0 = e.z;
  return true;
}
float texelM(int c) { return ${f1(levelTexel(0))} * exp2(float(c)); }
`;
// the far snow (the volume's, and the sprites' through the band): albedo × E
// / W clamped, × W × look.snow.gain
export const SNOW_COL_GLSL = `
vec3 snowCol(vec3 E) { return min(${v3(SNOW_ALB)} * E / uWhite, vec3(1.0)) * uWhite * uSnowGain; }
`;

const COMMON = `
precision highp usampler2DArray;
#define FV_N ${CANOPY_N}
#define FV_HQ ${f1(H_Q)}
#define FV_PAD ${f1(TOP_PAD_M)}
#define FV_L0 ${MARCH_L0}
#define FV_LMAX ${MARCH_LMAX}
#define FV_EPS ${f1(MARCH_EPS)}
#define FV_MAX ${MARCH_MAX}
#define FV_COVER ${f1(PHONE_COVER)}
#define FV_CL ${CANOPY_LEVELS}
#define FV_INF 1e30
#define FV_DROP ${f1(ROUND_DROP)}
#define FV_NS ${ROUND_NS}
#define FV_BIS ${ROUND_BIS}
uniform highp usampler2DArray uH;   // canopy H: (y − y0) × H_Q, max-mips
uniform int uSteps;
${CANOPY_LOOKUP_GLSL}`;

const VS = `${COMMON}
in float aInfo;                     // flag (0 lid, 1 wall top, 2 wall foot) + 4 × the shell level index
in float aSink;                     // THE SEAT: a wall foot's drop under the drawn ground (m; 0 elsewhere)
uniform float uSeat;                // 1: the seat on (__fv.seat, the probe's A/B)
#ifndef FRAG_DEPTH
uniform float uNear, uFade;
#endif
out vec3 vW;
out float vFlag;
const int PL[${PL.length}] = int[${PL.length}](${PL.join(', ')});
// forestmarch phoneLidH, line for line
float phoneLidH(int layer, float u, float v, float h, int l) {
  int s = FV_N >> l; float c = exp2(float(l));
  int i0 = int(floor(u / c - 0.5)), j0 = int(floor(v / c - 0.5));
  uint m = 0u;
  for (int b = 0; b < 2; b++) for (int a = 0; a < 2; a++) {
    int i = min(s - 1, max(0, i0 + a)), j = min(s - 1, max(0, j0 + b));
    m = max(m, texelFetch(uH, ivec3(i, j, layer), l).r);
  }
  return min(h, float(m) + FV_PAD * FV_HQ);
}
void main() {
  vec3 p = position;
  float fl = mod(aInfo, 4.0);
  if (fl > 1.5) p.y -= aSink * uSeat;   // a wall foot onto the DRAWN ground (forestmarch THE SEAT)
  if (fl < 1.5) {
    // lid + wall top: the march raises them TOP_PAD (the H quantum); steps 0
    // sinks them to the canopy (the plain shell, its top on the crowns)
    if (uSteps > 0) p.y += FV_PAD;
    else {
      ivec4 T; float y0;
      if (tileOf(ivec2(floor(p.xz / FV_IND)), T, y0)) {
        vec2 q = (p.xz - vec2(T.zw) * FV_IND) / texelM(T.y);
        int lv = int(floor(aInfo / 4.0));
        p.y = y0 + phoneLidH(T.x, q.x, q.y, (p.y - y0) * FV_HQ, PL[lv * FV_CL + T.y]) / FV_HQ;
      }
    }
  }
  vW = (modelMatrix * vec4(p, 1.0)).xyz;
  vFlag = fl;
#ifdef FRAG_DEPTH
  gl_Position = projectionMatrix * viewMatrix * vec4(vW, 1.0);
#else
  vec3 pr = vW - cameraPosition;
  float pd = length(pr.xz);
  vec3 pw = vW + pr * (${f1(HO_PUSH_M)} * (1.0 - clamp((pd - uNear + uFade) / max(uFade, 1e-3), 0.0, 1.0)) / max(length(pr), 1.0));
  gl_Position = projectionMatrix * viewMatrix * vec4(pw, 1.0);
#endif
}`;

const FS = `${COMMON}
layout(location = 0) out highp vec4 pc_fragColor;
#define gl_FragColor pc_fragColor
#ifndef VIEW_ID
uniform mat4 projectionMatrix;   // a multiview program (xrmv.js) has it per view from three's prefix
#endif
uniform highp sampler2DArray uA, uB;
uniform float uRange, uNear, uFade, uWhite, uPixAng, uGain, uSat, uSnowGain;
// THE SPLATS (splatforest.js): nearer than uSplat a crown tile the splats hold
// (uSplatCov: 1024 m crown tiles, 255 = in) is theirs — the volume draws none of it
uniform float uSplat;
uniform sampler2D uSplatCov;
bool splatCov(vec2 xz) {
  ivec2 q = ivec2(floor(xz / ${f1(SPLAT_TILE_M)})), n = textureSize(uSplatCov, 0);
  return q.x >= 0 && q.y >= 0 && q.x < n.x && q.y < n.y && texelFetch(uSplatCov, q, 0).r > 0.5;
}
uniform highp sampler2D uCdb;       // THE DEPTH CUT: the pass's zCover per cell (1e30 = not covered)
uniform mat3 uCdbM;                 // world → the cull key's camera axes
uniform vec4 uCdbRect;              // X0, dX, Y0, dY (tangent space of the key camera)
uniform vec2 uCdbN;                 // cells (cols, rows)
uniform int uCdbOn;                 // 1: the culled main pass
#define FV_CUTQ ${CUT_Q.toExponential(8)}
#define FV_CUTPAD ${f1(CUT_PAD_M)}
#define FV_CUTEYE ${f1(CUT_EYE_M)}
uniform int uDith;                  // THE COVER (forestmarch): bit 1 an exhausted hit kept by its cell's coverage, bit 2 the rim softened (__fv.dith)
uniform int uDbg;                   // __fv.debug: 0 the look, 1 why (hit / out of steps × empty A), 2 the level L, 4 the shade level, 5 / 6 the entry (tile, canopy, height), 7 the hand-off band (r in N, g out of steps, b nearer than N − F; no dither), 8 the terminal's normal (rgb = n · ½ + ½; black: no surface — an exhausted coarse hit)
uniform vec4 uSeason[2];            // season.js packSeason
uniform vec3 uSky, uSun, uL;        // meta.tex.light, toward the sun
#ifdef USE_FOG
uniform vec3 fogColor;
#ifdef FOG_EXP2
uniform float fogDensity;
#else
uniform float fogNear, fogFar;
#endif
#endif
in vec3 vW;
in float vFlag;
${SNOW_GLSL}${SNOW_COL_GLSL}
// the hand-off (handoff.js): the sprites draw the complementary pixels
${HANDOFF_GLSL}
// THE ROUNDED TERMINAL (forestmarch windowH / surfRef / cellHitRef, line for
// line): the level-0 cell's 3×3 window, the blended surface, the segment hit
// (the window's texels are fetched where used — the texture cache holds them; a
// dynamically indexed local array spills on some GPUs)
int gL;                                // the window's layer
float wH(ivec2 c) { return float(texelFetch(uH, ivec3(clamp(c, ivec2(0), ivec2(FV_N - 1)), gL), 0).r); }
void windowH(int layer, ivec2 c) { gL = layer; }
// -> s (H units, capped at the texel's own H); c the coverage, sh the blend
// before the rim drop, su uncapped (the shading's smooth surface)
float surf(ivec2 cc, vec2 p, float drop, out float c, out float sh, out float su) {
  float hc = wH(cc);
  c = 0.0; sh = 0.0; su = -1.0;
  if (hc <= 0.0) return -1.0;          // an empty texel paints nothing
  vec2 x = p - 0.5, i0 = floor(x), f = x - i0;
  ivec2 o = clamp(ivec2(i0), cc - 1, cc);   // windowH's clamp: the 4 centres stay in the 3×3 window
  for (int b = 0; b < 2; b++) for (int a = 0; a < 2; a++) {
    float hk = wH(o + ivec2(a, b));
    if (hk <= 0.0) continue;
    float w = (a == 1 ? f.x : 1.0 - f.x) * (b == 1 ? f.y : 1.0 - f.y);
    c += w; sh += w * hk;
  }
  sh /= c;
  float e = clamp((c - 0.5) / 0.5, 0.0, 1.0), g = e * e * (3.0 - 2.0 * e);
  su = sh - drop * (1.0 - g);
  return min(su, hc);
}
bool insideAt(ivec2 cc, vec3 q, vec3 d, float a, float drop) {
  float c, sh, su;
  float s = surf(cc, q.xz + d.xz * a, drop, c, sh, su);
  return c >= 0.5 && q.y + d.y * a <= s;
}
float cellHit(ivec2 cc, vec3 q, vec3 d, float len, float drop, float fp) {
  if (insideAt(cc, q, d, 0.0, drop)) return 0.0;
  int ns = clamp(int(ceil(len * length(d.xz) / fp)), 1, FV_NS), nb = fp >= 1.0 ? 1 : FV_BIS;
  float lo = 0.0;
  for (int n = 1; n <= FV_NS; n++) {
    if (n > ns) break;
    float hi = len * float(n) / float(ns);
    if (!insideAt(cc, q, d, hi, drop)) { lo = hi; continue; }
    for (int b = 0; b < FV_BIS; b++) { if (b >= nb) break; float m = 0.5 * (lo + hi); if (insideAt(cc, q, d, m, drop)) hi = m; else lo = m; }
    return hi;
  }
  return -1.0;
}
// the 'exit' tile switch: the finest tile at the current point (in integer
// cells: no float round-trip through world metres); false: none, or the
// point outside it
bool switchTile(inout ivec4 T, inout float y0, inout float t, inout float u, inout float v, inout float h) {
  vec2 lm = vec2(u, v) * t;
  ivec4 T2; float y2;
  if (!tileOf(T.zw + ivec2(floor(lm / FV_IND)), T2, y2)) return false;
  float t2 = texelM(T2.y), N = float(FV_N);
  vec2 l2 = lm + vec2(T.zw - T2.zw) * FV_IND;
  u = l2.x / t2; v = l2.y / t2; h += (y0 - y2) * FV_HQ;
  T = T2; y0 = y2; t = t2;
  return !(u < 0.0 || v < 0.0 || u >= N || v >= N);
}
// THE CLIMB (forestmarch marchRef): up from level L while the point lies
// over the next coarser box, at most cap levels; hc = the last climbed box's
// height (the next step's fetch, reused), −1 none
int climbL(int L, int cap, int layer, float u, float v, float h, inout float hc) {
  hc = -1.0;
  for (int n = 0; n < FV_LMAX; n++) {
    if (n >= cap || L >= FV_LMAX) break;
    float hn = float(texelFetch(uH, ivec3(ivec2(floor(vec2(u, v))) >> (L + 1), layer), L + 1).r);
    if (h <= hn) break;
    L++; hc = hn;
  }
  return L;
}
void main() {
  ivec4 T; float y0;
  if (!tileOf(ivec2(floor(vW.xz / FV_IND)), T, y0)) discard;   // no canopy tile under the entry
  float N = float(FV_N), t = texelM(T.y);
  float u = (vW.x - float(T.z) * FV_IND) / t, v = (vW.z - float(T.w) * FV_IND) / t, h = (vW.y - y0) * FV_HQ;
  vec3 D = normalize(vW - cameraPosition);
  float du = D.x / t, dh = D.y * FV_HQ, dv = D.z / t;
  float nudge = FV_EPS / max(max(abs(du), abs(dv)), 1e-9);
  float pk = max(uPixAng * length(vW - cameraPosition), 1e-4);   // the pixel footprint (m) at the entry: cellHit's sample spacing
  if (uDbg == 5 || uDbg == 6) {
    // the entry, before any march: the tile, its canopy, the entry height
    ivec2 e0 = ivec2(clamp(floor(vec2(u, v)), vec2(0.0), vec2(N - 1.0)));
    float hx = float(texelFetch(uH, ivec3(0, 0, T.x), FV_LMAX).r);
    float h0 = float(texelFetch(uH, ivec3(e0, T.x), 0).r), h4 = float(texelFetch(uH, ivec3(e0 >> 4, T.x), 4).r);
    pc_fragColor = uDbg == 5 ? vec4(1.0, hx > 0.0 ? 1.0 : 0.0, clamp((h - hx) / FV_HQ / 64.0, 0.0, 1.0), 1.0)
                             : vec4(1.0, h0 > 0.0 ? 1.0 : 0.0, h4 > 0.0 ? 1.0 : 0.0, 1.0);
    return;
  }
  int L = FV_L0;
  float tt = 0.0;                      // the ray parameter travelled (forestmarch cellSpan's −tt)
  bool ex = uSteps > 0;                // out of steps (the loop's breaks clear it)
  bool surfHit = false;                // the hit lies on the rounded terminal (the window of cell wc, layer gL)
  ivec2 wc = ivec2(0);
  if (uSteps <= 0) {
    // terminalRef at the entry point (la 0, lw 1); a lid point under a
    // rising ray only within TOP_PAD of the canopy at lw (no lid in the sky)
    bool wall = vFlag > 0.5;
    int l = wall ? 1 : 0, s = FV_N >> l;
    float c = exp2(float(l));
    ivec3 at = ivec3(min(s - 1, max(0, int(floor(u / c)))), min(s - 1, max(0, int(floor(v / c)))), T.x);
    ivec3 a1 = ivec3(min((FV_N >> 1) - 1, max(0, int(floor(u / 2.0)))), min((FV_N >> 1) - 1, max(0, int(floor(v / 2.0)))), T.x);
    bool hit = wall ? h <= float(texelFetch(uH, at, l).r)
                    : texelFetch(uA, at, 0).a >= FV_COVER && (D.y < 0.0 || h <= float(texelFetch(uH, a1, 1).r) + FV_PAD * FV_HQ);
    if (!hit) discard;
    L = l;
  } else {
    // marchRef; out of steps: below (a hit only inside the canopy envelope)
    // THE DEPTH CUT (forestmarch cutXZ / cutPast): past xc (m from the eye,
    // horizontal) every hit of this ray fails the depth test at every sample
    // of the pixel (the max zCover under the ray ± a pixel), or lies past the range
    float xc = uRange;
    if (uCdbOn > 0) {
      vec3 dk = uCdbM * D;
      if (dk.z < -1e-6) {
        vec2 X = dk.xy / -dk.z;
        float e = uPixAng * (1.0 + dot(X, X));
        ivec2 a = ivec2(floor((X - e - uCdbRect.xz) / uCdbRect.yw)), b = ivec2(floor((X + e - uCdbRect.xz) / uCdbRect.yw));
        if (a.x >= 0 && a.y >= 0 && b.x < int(uCdbN.x) && b.y < int(uCdbN.y)) {
          float zb = max(max(texelFetch(uCdb, a, 0).r, texelFetch(uCdb, ivec2(b.x, a.y), 0).r),
                         max(texelFetch(uCdb, ivec2(a.x, b.y), 0).r, texelFetch(uCdb, b, 0).r)) + FV_CUTEYE;
          float den = projectionMatrix[3][2] / zb + 2.0 * FV_CUTQ, cosT = -(mat3(viewMatrix) * D).z;
          if (zb < 1e29 && den < 0.0 && cosT > 0.0) xc = min(xc, projectionMatrix[3][2] / den / cosT * length(D.xz));
        }
      }
    }
    float hc = -1.0;     // the climb's box height at (u, v, L): this step's fetch
    bool fresh = true;   // the start (or a tile switch): climb from FV_L0
    for (int k = 0; k < FV_MAX; k++) {
      if (k >= uSteps) break;
      if (length(vec2(T.zw) * FV_IND + vec2(u, v) * t - cameraPosition.xz) - FV_CUTPAD > xc) discard;   // 'cut'
      if (u < 0.0 || v < 0.0 || u >= N || v >= N) {
        if (!switchTile(T, y0, t, u, v, h)) discard;   // none: the floor below, a miss
        du = D.x / t; dv = D.z / t;
        nudge = FV_EPS / max(max(abs(du), abs(dv)), 1e-9);
        L = FV_L0;
        fresh = true;
        tt = FV_INF;   // the start lies in the tile behind
      }
      if (h < 0.0) discard;   // 'below'
      if (fresh) { L = climbL(FV_L0, FV_LMAX, T.x, u, v, h, hc); fresh = false; }
      float cs = exp2(float(L));
      int ci = int(floor(u / cs)), cj = int(floor(v / cs));
      if (L == 0) {
        // the rounded terminal: the old box's candidate (the ray under the texel's own H), refined on the surface capped at it
        wc = ivec2(ci, cj);
        windowH(T.x, wc);
        float hw = wH(wc), fi = float(ci), fj = float(cj);
        float tx = du > 0.0 ? (fi + 1.0 - u) / du : du < 0.0 ? (fi - u) / du : FV_INF;
        float tz = dv > 0.0 ? (fj + 1.0 - v) / dv : dv < 0.0 ? (fj - v) / dv : FV_INF;
        float bx = du > 0.0 ? (fi - u) / du : du < 0.0 ? (fi + 1.0 - u) / du : -FV_INF;
        float bz = dv > 0.0 ? (fj - v) / dv : dv < 0.0 ? (fj + 1.0 - v) / dv : -FV_INF;
        float tin = min(0.0, max(max(bx, bz), -tt)), te = min(tx, tz);
        if (te >= FV_INF) discard;   // 'up'
        if (hw > 0.0 && min(h + dh * tin, h + dh * te) <= hw) {
          float a = cellHit(wc, vec3(u + du * tin, h + dh * tin, v + dv * tin), vec3(du, dh, dv), te - tin, FV_DROP * t * FV_HQ, pk / t);
          if (a >= 0.0) { float b = tin + a; u += du * b; h += dh * b; v += dv * b; ex = false; surfHit = true; break; }   // 'hit'
        }
        if (hw <= 0.0 && dh < 0.0 && -h / dh < te) discard;   // 'floor': an empty texel, the ray reaches the tile floor
        float a = te + nudge;
        u += du * a; h += dh * a; v += dv * a; tt += a;
        L = 1;   // the old box miss: on at level 1
        continue;
      }
      float hm = hc >= 0.0 ? hc : float(texelFetch(uH, ivec3(ci, cj, T.x), L).r);
      hc = -1.0;
      if (h <= hm) {
        L--;
        continue;
      }
      float lx = float(ci) * cs, lz = float(cj) * cs;
      float tx = du > 0.0 ? (lx + cs - u) / du : du < 0.0 ? (lx - u) / du : FV_INF;
      float tz = dv > 0.0 ? (lz + cs - v) / dv : dv < 0.0 ? (lz - v) / dv : FV_INF;
      float te = min(tx, tz);
      if (dh < 0.0) {
        float tp = (hm - h) / dh;
        if (tp < te) {
          if (hm <= 0.0) discard;   // 'floor': an empty box, the ray reaches the tile floor
          u += du * tp; v += dv * tp; h = hm; tt += tp;
          L--;
          continue;
        }
      }
      if (te >= FV_INF) discard;   // 'up'
      float a = te + nudge;
      u += du * a; h += dh * a; v += dv * a; tt += a;
      if (u >= 0.0 && v >= 0.0 && u < N && v < N) L = climbL(L, 1, T.x, u, v, h, hc);   // up only over the coarser box
    }
    // out of steps: a hit only inside the canopy envelope — a falling ray
    // under its level-L box top, a rising one at the start level (h ≤
    // H[min(L, FV_L0)]: the air over a grazed crest stays air); an empty
    // box or above it a miss (marchRef 'steps-empty' / 'steps-above')
    if (ex) {
      if ((u < 0.0 || v < 0.0 || u >= N || v >= N) && !switchTile(T, y0, t, u, v, h)) discard;
      if (L == 0) {
        // out of steps at level 0: the envelope is the surface itself
        wc = ivec2(floor(vec2(u, v)));
        windowH(T.x, wc);
        if (wH(wc) <= 0.0 || !insideAt(wc, vec3(u, h, v), vec3(0.0), 0.0, FV_DROP * t * FV_HQ)) discard;
        surfHit = true;
      } else {
        int le = dh < 0.0 ? L : min(L, FV_L0);
        uint he = texelFetch(uH, ivec3(ivec2(floor(vec2(u, v))) >> le, T.x), le).r;
        if (he == 0u || h > float(he)) discard;
      }
    }
  }
  vec3 P = vec3(float(T.z) * FV_IND + u * t, y0 + h / FV_HQ, float(T.w) * FV_IND + v * t);
  vec3 R = P - cameraPosition;
  float dxz = length(R.xz);
  if (dxz > uRange) discard;
  if (dxz < uSplat && splatCov(P.xz)) discard;   // the splats' (splatforest.js)
  if (uDbg == 7) { pc_fragColor = vec4(dxz < uNear ? 1.0 : 0.0, ex ? 1.0 : 0.0, dxz < uNear - uFade ? 1.0 : 0.0, 1.0); return; }
  if (!hoVolKeep(dxz, true)) discard;   // a tile: T holds the hit; whole across the band (handoff.js)
  // the hit's OWN cell, nearest, at the footprint level ≥ L: a filter would
  // blend the coverage-normalised colour with the empty cells' rgb 0 (dark
  // crown edges); the hit's box holds canopy, so does every coarser cell. An
  // exhausted hit starts at the footprint (not L's 2^L-texel mip: the far
  // lumps) and climbs to the first covered cell, ≤ L (marchRef shadeLevelRef)
  float lo = ex ? 0.0 : float(L);
  float lod = max(lo, log2(max(uPixAng * length(R) / t, 1.0)));
  int lf = clamp(int(floor(lod + 0.5)), int(lo), FV_LMAX);
  ivec2 hc = ivec2(clamp(floor(vec2(u, v)), vec2(0.0), vec2(N - 1.0)));
  if (ex) for (int s = 0; s < ${SHADE_UP}; s++) { if (lf >= L || texelFetch(uA, ivec3(hc >> lf, T.x), lf).a >= ${f1(SHADE_COV)}) break; lf++; }
  vec4 A = texelFetch(uA, ivec3(hc >> lf, T.x), lf), B = texelFetch(uB, ivec3(hc >> lf, T.x), lf);
  // THE COVER (forestmarch coverKeep): an exhausted hit stands for a coarse
  // box, not a surface — it keeps the pixel with the probability of the real
  // crown cover of its shade cell (the hand-off's per-pixel noise): a sparse
  // stand reads as crowns and gaps, never as a solid block
  if ((uDith & 1) != 0 && ex && hoDither(gl_FragCoord.xy) >= A.a) discard;
  vec3 col = A.rgb * uWhite;
  col = mix(vec3(dot(col, ${v3(VEG_LUMA)})), col, uSat) * uGain;
  // the rounded terminal's shape in the light: A is lit for the texel's own
  // crown normal (an upward reference here), the surface's normal relights it
  // relative to that (ROUND_KA floor, clamped ROUND_K), its rim / the wall
  // under it darken toward ROUND_RIM (the blend's height minus the hit's)
  vec3 nS = vec3(0.0);
  if (surfHit) {
    float drop = FV_DROP * t * FV_HQ, c0, sh0, c1, sh1, c2, sh2, s0, sa, sb, x0, x1, x2, x3;
    ivec2 cc = wc;
    vec2 lo = vec2(cc), hi = lo + 1.0, p = clamp(vec2(u, v), lo, hi);
    x0 = surf(cc, p, drop, c0, sh0, s0);   // the uncapped surface: the round shading
    if ((uDith & 2) != 0 && hoDither(gl_FragCoord.xy) >= smoothstep(0.5, 0.85, c0)) discard;   // THE COVER: a soft rim
    vec2 px0 = clamp(p - vec2(0.05, 0.0), lo, hi), px1 = clamp(p + vec2(0.05, 0.0), lo, hi);
    vec2 pz0 = clamp(p - vec2(0.0, 0.05), lo, hi), pz1 = clamp(p + vec2(0.0, 0.05), lo, hi);
    x0 = surf(cc, px1, drop, c1, sh1, x1); x0 = surf(cc, px0, drop, c2, sh2, x2);
    sa = x1 - x2; float ca = c1 - c2;
    x0 = surf(cc, pz1, drop, c1, sh1, x1); x0 = surf(cc, pz0, drop, c2, sh2, x2);
    sb = x1 - x2; float cb = c1 - c2;
    vec2 g = vec2(sa / max(px1.x - px0.x, 1e-4), sb / max(pz1.y - pz0.y, 1e-4)) / (FV_HQ * t);
    vec2 gc = vec2(ca / max(px1.x - px0.x, 1e-4), cb / max(pz1.y - pz0.y, 1e-4));
    // on the coverage wall (the hit under the surface): its horizontal normal
    nS = s0 - h > max(4.0, 0.05 * drop) && dot(gc, gc) > 0.0 ? normalize(vec3(-gc.x, 0.0, -gc.y)) : normalize(vec3(-g.x, 1.0, -g.y));
    float kl = (${f1(ROUND_KA)} + ${f1(1 - ROUND_KA)} * max(dot(nS, uL), 0.0)) / (${f1(ROUND_KA)} + ${f1(1 - ROUND_KA)} * max(uL.y, 0.0));
    col *= clamp(kl, ${f1(ROUND_K[0])}, ${f1(ROUND_K[1])}) * (1.0 - ${f1(ROUND_RIM)} * clamp((sh0 - h) / drop, 0.0, 1.0));
  }
  // snow on the canopy top: aspect 0 / slope 0 (the tile keeps no normal —
  // B.b, the crown's catch, carries its form + upward normal); lit by
  // canopygen's E on an upward normal (w 1, n·L = L.y), the same / W clamp
  vec3 E = B.r * uSky + uSun * (max(uL.y, 0.0) * B.g);
  col = mix(col, snowCol(E), snowWeight(P.y, 0.0, 0.0, B.b));
  gl_FragColor = vec4(col, 1.0);
  vec3 vp = mat3(viewMatrix) * R;
#ifdef FRAG_DEPTH
  vec4 cp = projectionMatrix * vec4(vp * (1.0 + hoPush(dxz) / max(length(R), 1.0)), 1.0);
  gl_FragDepth = clamp(0.5 * cp.z / cp.w + 0.5, 0.0, 1.0);
#endif
#include <tonemapping_fragment>
#include <colorspace_fragment>
#ifdef USE_FOG
  float fd = -vp.z;
#ifdef FOG_EXP2
  float fogFactor = 1.0 - exp(-fogDensity * fogDensity * fd * fd);
#else
  float fogFactor = smoothstep(fogNear, fogFar, fd);
#endif
  gl_FragColor.rgb = mix(gl_FragColor.rgb, fogColor, fogFactor);
#endif
  if (uDbg == 1) gl_FragColor = vec4(ex ? 1.0 : 0.0, ex ? 0.0 : 1.0, A.a < 0.02 ? 1.0 : 0.0, 1.0);
  else if (uDbg == 2) gl_FragColor = vec4(vec3(float(L) / 8.0), 1.0);
  else if (uDbg == 4) gl_FragColor = vec4(vec3(float(lf) / 8.0), 1.0);
  else if (uDbg == 8) gl_FragColor = vec4(surfHit ? nS * 0.5 + 0.5 : vec3(0.0), 1.0);
}`;

// ---------------------------------------------------------------- the draw
// a draw group's signature after a world swap spoiled it (invalidateTiles):
// no reconcile ever computes it, so the group is built again
const SWAP_SIG = '#swap';
export class ForestVolume {
  // opt.weapons / opt.cull (the VisCull: THE DEPTH CUT) may be getters
  // (main.js builds them later); opt.sea:
  // the world's season data (SEA1, season.js; main.js boot) — required
  constructor(opt) {
    const { meta } = opt;
    this.sea = readSea(opt.sea);
    this.ix = shellIndex(meta.forest.shell);   // throws on a non-FSH1 shell
    const lt = meta.tex && meta.tex.light;
    if (!(lt && lt.white > 0)) throw new Error('forestvol: world meta.tex.light.white missing');
    const L = sunDirFromLighting(meta.lighting);
    if (!L) throw new Error('forestvol: world meta.lighting missing');
    const bb = meta.bbox;
    if (!bb) throw new Error('forestvol: world meta.bbox missing');
    this.opt = opt;
    this.meta = meta;
    this.baseUrl = opt.baseUrl;
    // the indirection over the world frame (0, 0)..(e1 − e0, n1 − n0)
    this.indN = [Math.max(1, Math.ceil((bb.e1 - bb.e0) / IND_M)), Math.max(1, Math.ceil((bb.n1 - bb.n0) / IND_M))];
    this.ind = new THREE.DataTexture(new Float32Array(4 * this.indN[0] * this.indN[1]), this.indN[0], this.indN[1], THREE.RGBAFormat, THREE.FloatType);
    this.ind.minFilter = this.ind.magFilter = THREE.NearestFilter;
    this.ind.generateMipmaps = false;
    this.ind.needsUpdate = true;
    this._indKey = '';
    // until the canopy pool exists: 1×1 stand-ins of the pool's formats
    const ph = (data, fmt, type, internal, cs) => {
      const t = new THREE.DataArrayTexture(data, 1, 1, 1);
      t.format = fmt; t.type = type; t.colorSpace = cs;
      if (internal) t.internalFormat = internal;
      t.minFilter = t.magFilter = THREE.NearestFilter;
      t.generateMipmaps = false;
      t.needsUpdate = true;
      return t;
    };
    this.ph = {
      A: ph(new Uint8Array(4), THREE.RGBAFormat, THREE.UnsignedByteType, null, THREE.SRGBColorSpace),
      B: ph(new Uint8Array(4), THREE.RGBAFormat, THREE.UnsignedByteType, null, THREE.NoColorSpace),
      H: ph(new Uint16Array(1), THREE.RedIntegerFormat, THREE.UnsignedShortType, 'R16UI', THREE.NoColorSpace),
    };
    this.U = {
      ...THREE.UniformsUtils.clone(THREE.UniformsLib.fog),
      uH: { value: this.ph.H }, uA: { value: this.ph.A }, uB: { value: this.ph.B }, uInd: { value: this.ind },
      uIndOrigin: { value: new THREE.Vector2(0, 0) }, uIndN: { value: new THREE.Vector2(this.indN[0], this.indN[1]) },
      uSteps: { value: 0 }, uDbg: { value: 0 }, uSeat: { value: 1 }, uDith: { value: 1 }, uRange: { value: 0 }, uNear: HANDOFF.uNear, uFade: HANDOFF.uFade,
      uWhite: { value: lt.white }, uPixAng: { value: 0 },
      uCdb: { value: null }, uCdbM: { value: new THREE.Matrix3() }, uCdbRect: { value: new THREE.Vector4() },
      uCdbN: { value: new THREE.Vector2() }, uCdbOn: { value: 0 },
      uGain: { value: 1 }, uSat: { value: 1 }, uSnowGain: { value: 1 },
      uSplat: { value: 0 }, uSplatCov: { value: splatCovNone() },
      uSeason: SEASON.uSeason,            // THE world season (season.js; the sprites hold it too)
      uSky: { value: new THREE.Vector3(...lt.sky) }, uSun: { value: new THREE.Vector3(...lt.sun) }, uL: { value: new THREE.Vector3(...L) },
    };
    this.cls = deviceClass();
    this.mat = this._material(this.cls);
    this.root = new THREE.Group();
    this.root.name = 'forestvol';
    this.root.matrixAutoUpdate = false;
    famScene(opt.scene, 'trees').add(this.root);
    // the keeper: never drawn, never disposed — every warm-up pass (canvas +
    // optic target) compiles the program before the first group lands
    const kg = new THREE.BufferGeometry();
    kg.setAttribute('position', new THREE.BufferAttribute(new Float32Array(9), 3));
    kg.setAttribute('aInfo', new THREE.BufferAttribute(new Float32Array(3), 1));
    kg.setAttribute('aSink', new THREE.BufferAttribute(new Float32Array(3), 1));
    this.keeper = new THREE.Mesh(kg, this.mat);
    this.keeper.name = 'forestvol-keeper';
    this.keeper.visible = false;
    this.keeper.frustumCulled = false;
    this.keeper.matrixAutoUpdate = false;
    this.root.add(this.keeper);
    this.root.updateMatrixWorld(true);
    this.plan = null;             // the last shellPlan (tiles nearest first)
    this.planAt = null;
    this.skirtsOf = new Map();    // 'i_j' -> [side] (the plan's skirts)
    this.lru = new Map();         // 'c_i_j' -> decoded FSH1 tile (+ edges), use order
    this.lruCap = LRU_MIN;
    this.pend = new Set();        // 'c_i_j' in flight
    this.fail = new Map();        // 'c_i_j' -> the time a fetch failed / was dropped
    this.bad = new Set();         // 'c_i_j' that failed to decode (not asked again)
    this._stale = new Set();      // 'c_i_j' in flight a world swap invalidated (invalidateTiles)
    this.drawn = new Map();       // 'i_j' -> the decoded tile drawn (its level)
    this.groups = new Map();      // 'gx_gz' -> {mesh, sig, tris, skirts, tiles}
    this.dirty = new Map();       // 'gx_gz' -> the signature to build ('' = remove)
    this._job = null;             // the group build on the pool: { g, sig } (one at a time)
    this.steps = { main: 0, optic: 0 };
    this._liveKey = '';
    this._liveDirty = true;
    this._replan = true;
    this._recon = false;
    this._scan = false;
    this.stats = { fetched: 0, failed: 0, builds: 0, ind: 0, cdb: 0, seated: 0, seat_ms: 0 };
    this._seatG = null;           // THE SEAT: the group being re-read ({ g, gen, k })
    this._cdbKey = -1;            // the cull key whose zCover uCdb holds
    this.dbgCut = true;           // __fv.cut (the probe's A/B)
    this.U.uCdb.value = this._cdbTex(1, 1);
    onGenChange(() => { this._liveDirty = true; });
  }

  _material(cls) {
    return new THREE.ShaderMaterial({
      name: 'forest-vol', glslVersion: THREE.GLSL3, vertexShader: VS, fragmentShader: FS, uniforms: this.U,
      defines: depthMode(cls) === 'frag' ? { FRAG_DEPTH: '' } : {}, side: THREE.FrontSide, fog: true,
    });
  }

  // ---- once a tick (main.js, before genFrame)
  tick(pos) {
    const live = genLive();
    if (!live || !live.vol || !pos) return;
    if (this._liveDirty) this._applyLive(live);
    const p = this.planAt;
    if (this._replan || !p || Math.hypot(pos.x - p.x, pos.z - p.z) >= REPLAN_M) this._plan(live, pos);
    if (this._scan) this._fetch();
    if (this._recon) this._reconcile();
    this._buildOne(pos);
    this._seat(pos);
    this._splatVis();
  }

  // THE SPLATS (splatforest.js): a group wholly theirs (the splats reach the
  // volume's range and hold every crown tile of the group) is not drawn; a
  // part held discards per fragment (uSplat / uSplatCov) — no double draw
  _splatVis() {
    const sp = this.splat, U = this.U;
    U.uSplat.value = sp ? sp.reach : 0;
    U.uSplatCov.value = sp ? sp.tex : U.uSplatCov.value;
    const all = !!sp && sp.reach >= U.uRange.value;
    for (const [g, e] of this.groups) {
      let hide = false;
      if (all) { const [gx, gz] = g.split('_').map(Number); hide = sp.holds(gx, gz, SHELL_GROUP_M); }
      e.mesh.visible = !hide;
    }
  }

  // THE SEAT (forestmarch.js): each group's wall feet re-read off the drawn
  // ground (the ground tree, the sprites' own groundDrawn) whenever the
  // ground tree moved since the group's last read — ONE group at a time,
  // nearest first, SEAT_MS a tick; a foot over undrawn ground keeps its sink.
  // The sink is an attribute: one upload per re-read group that changed
  _seat(pos) {
    const G = GROUND;
    if (!G || !this.groups.size) return;
    const gen = Number.isFinite(G.gen) ? G.gen : 0;
    let job = this._seatG;
    if (job && !this.groups.has(job.g)) job = this._seatG = null;
    if (!job) {
      let best = null, bd = Infinity;
      for (const [g, e] of this.groups) {
        if (e.seatGen === gen || !e.feet) continue;
        const [gx, gz] = g.split('_').map(Number);
        const d = Math.hypot((gx + 0.5) * SHELL_GROUP_M - pos.x, (gz + 0.5) * SHELL_GROUP_M - pos.z);
        if (d < bd) { bd = d; best = g; }
      }
      if (best === null) return;
      job = this._seatG = { g: best, gen, k: 0, moved: false };
    }
    const e = this.groups.get(job.g), feet = e.feet, P = e.mesh.geometry.attributes.position.array;
    const A = e.mesh.geometry.attributes.aSink, S = A.array, t0 = performance.now();
    let k = job.k;
    for (; k < feet.length; k++) {
      if ((k & 63) === 0 && performance.now() - t0 > SEAT_MS) break;
      const v = feet[k], s = seatSink(P[3 * v + 1], G.groundDrawn(P[3 * v], P[3 * v + 2]));
      if (s !== null && s !== S[v]) { S[v] = s; job.moved = true; }
    }
    this.stats.seat_ms += performance.now() - t0;
    job.k = k;
    if (k < feet.length) return;
    if (job.moved) A.needsUpdate = true;
    e.seatGen = job.gen;
    this.stats.seated++;
    this._seatG = null;
  }

  // the live view's draw values (uniforms, steps, the class's depth mode); a
  // bad block refuses the change (logged), never the frame
  _applyLive(live) {
    this._liveDirty = false;
    const key = JSON.stringify([live.cls, live.vol, live.shell, live.sprites, live.look, live.season]);
    if (key === this._liveKey) return;
    try {
      const U = this.U, lk = live.look;
      U.uRange.value = live.vol.range_m || 0;
      setHandoffView((live.sprites && live.sprites.view_m) || 0, (live.sprites && live.sprites.fade_m) || 0);
      U.uGain.value = lk.canopy.gain; U.uSat.value = lk.canopy.sat; U.uSnowGain.value = lk.snow.gain;
      applySeason(live.season, this.sea);
      this.steps = { main: stepsFor(live, false), optic: stepsFor(live, true) };
    } catch (e) { console.error('[forestvol] live view:', e.message); return; }
    this._liveKey = key;
    this._replan = true;
    if (live.cls && live.cls !== this.cls) {
      // the depth mode is a define: a new program (rare — a class pin)
      const old = this.mat;
      this.cls = live.cls;
      this.mat = this._material(this.cls);
      this.keeper.material = this.mat;
      for (const g of this.groups.values()) g.mesh.material = this.mat;
      old.dispose();
    }
  }

  _plan(live, pos) {
    this._replan = false;
    this.planAt = { x: pos.x, z: pos.z };
    const range = live.vol.range_m || 0, T = this.ix.T;
    const plan = shellPlan(this.ix, pos.x, pos.z, shellRings(live.shell, range), range);
    const d = (t) => Math.hypot((t.i + 0.5) * T - pos.x, (t.j + 0.5) * T - pos.z);
    plan.tiles.sort((a, b) => d(a) - d(b));
    this.plan = plan;
    this.skirtsOf = new Map();
    for (const s of plan.skirts) {
      const k = `${s.i}_${s.j}`, a = this.skirtsOf.get(k);
      if (a) a.push(s.side); else this.skirtsOf.set(k, [s.side]);
    }
    this.lruCap = Math.max(LRU_MIN, LRU_K * plan.tiles.length);
    this._recon = true;
    this._scan = true;
  }

  // the plan's missing files, nearest first, FETCH_MAX in flight
  _fetch() {
    this._scan = false;
    const now = performance.now();
    for (const t of this.plan.tiles) {
      if (this.pend.size >= FETCH_MAX) return;
      const k = `${t.c}_${t.i}_${t.j}`;
      if (this.lru.has(k) || this.pend.has(k) || this.bad.has(k)) continue;
      if (now - (this.fail.get(k) ?? -Infinity) < RETRY_MS) { this._scan = true; continue; }
      this._get(t, k);
    }
  }

  _get(t, k) {
    this.pend.add(k);
    const url = `${this.baseUrl}/${this.ix.dirs.get(t.c)}/${t.i}_${t.j}.fsh`;
    fetchAsset(url, { prio: 'bulk' }).then((buf) => {
      if (!buf) throw new Error('dropped');
      // invalidated in flight (invalidateTiles): the old world's bytes —
      // dropped, the next scan asks again
      if (this._stale.delete(k)) return;
      // decoded on the pool (pool-forest.js forest.shell: the bytes go, its
      // arrays come back)
      return run('forest.shell', { buf }, { transfer: [buf] }).then((d) => {
        if (this._stale.delete(k)) return;
        if (d.cellM !== t.c || d.i !== t.i || d.j !== t.j) throw new Error(`holds ${d.cellM} m ${d.i}_${d.j}`);
        this.fail.delete(k);
        this.stats.fetched++;
        this._lruPut(k, d);
        this._recon = true;
      }, (e) => { if (!this._stale.delete(k)) this.bad.add(k); console.error(`[forestvol] ${url}: ${e.message}`); });
    }).catch(() => { this._stale.delete(k); this.fail.set(k, performance.now()); this.stats.failed++; })
      .finally(() => { this.pend.delete(k); this._scan = true; });
  }

  // R8h2 world swap (worldswap.js forestHook): the shell tiles of `rels`
  // (forest/shell/<c>/<i>_<j>.fsh) moved under the running client — each
  // leaves the LRU / the drawn set / bad / fail (one in flight is dropped as it
  // lands), its draw group is dirtied (the signature names tiles and levels,
  // not their bytes: the group's own is spoiled so the next reconcile rebuilds
  // it), and the volume plans again — its fetch streams exactly those again.
  // `shell` = the new meta.forest.shell (the same tile set and levels —
  // worldswap.js checked it —, a tile's triangle count may have moved).
  invalidateTiles(rels, shell = null) {
    if (shell) this.ix = shellIndex(shell);
    const cellOf = new Map([...this.ix.dirs].map(([c, dir]) => [dir, c]));
    const spoil = (g) => {
      const b = this.groups.get(g);
      if (b) b.sig = SWAP_SIG;
      if (this._job && this._job.g === g) this._job.sig = SWAP_SIG;
    };
    for (const r of rels) {
      const s = r.lastIndexOf('/'), c = cellOf.get(r.slice(0, s)), m = /^(-?\d+)_(-?\d+)\.fsh$/.exec(r.slice(s + 1));
      if (c === undefined || !m) continue;
      const ij = `${m[1]}_${m[2]}`, k = `${c}_${ij}`;
      this.lru.delete(k); this.bad.delete(k); this.fail.delete(k);
      if (this.pend.has(k)) this._stale.add(k);
      const d = this.drawn.get(ij);
      if (d && d.cellM === c) { this.drawn.delete(ij); spoil(this._gkey(d.i, d.j)); }
    }
    this._replan = true;
  }

  // the LRU: a Map in use order; a drawn tile is never evicted
  _lruPut(k, d) {
    this.lru.delete(k);
    this.lru.set(k, d);
    if (this.lru.size <= this.lruCap) return;
    const used = new Set(this.drawn.values());
    for (const [key, v] of this.lru) {
      if (this.lru.size <= this.lruCap) break;
      if (!used.has(v)) this.lru.delete(key);
    }
  }

  _gkey(i, j) { const T = this.ix.T; return `${Math.floor((i * T) / SHELL_GROUP_M)}_${Math.floor((j * T) / SHELL_GROUP_M)}`; }

  // the drawn set from the plan + the LRU (a tile whose new level has not
  // landed keeps its old one), then every group whose content moved is dirty
  _reconcile() {
    this._recon = false;
    const want = new Set();
    for (const t of this.plan.tiles) {
      const ij = `${t.i}_${t.j}`, k = `${t.c}_${ij}`;
      want.add(ij);
      const d = this.lru.get(k);
      if (!d) continue;
      this.lru.delete(k); this.lru.set(k, d);
      this.drawn.set(ij, d);
    }
    for (const ij of [...this.drawn.keys()]) if (!want.has(ij)) this.drawn.delete(ij);
    const sig = new Map();
    for (const [ij, d] of this.drawn) {
      const g = this._gkey(d.i, d.j), sk = this.skirtsOf.get(ij);
      const s = `${ij}:${d.cellM}:${sk ? sk.join('') : ''}`;
      const a = sig.get(g);
      if (a) a.push(s); else sig.set(g, [s]);
    }
    // a group on the pool counts as built with its job's signature (its
    // result lands only while the group is not dirty again)
    const job = this._job, building = (g) => !!job && job.g === g;
    for (const [g, a] of sig) {
      const s = a.sort().join(','), b = this.groups.get(g), cur = building(g) ? job.sig : b && b.sig;
      if (cur !== s) this.dirty.set(g, s); else this.dirty.delete(g);
    }
    for (const g of this.groups.keys()) if (!sig.has(g)) this.dirty.set(g, '');
    if (job && !sig.has(job.g)) this.dirty.set(job.g, '');
    for (const g of [...this.dirty.keys()]) if (!sig.has(g) && !this.groups.has(g) && !building(g)) this.dirty.delete(g);
  }

  // ONE group at a time (on the pool), the nearest dirty first
  _buildOne(pos) {
    if (!this.dirty.size || this._job) return;
    let best = null, bd = Infinity;
    for (const g of this.dirty.keys()) {
      const [gx, gz] = g.split('_').map(Number);
      const d = Math.hypot((gx + 0.5) * SHELL_GROUP_M - pos.x, (gz + 0.5) * SHELL_GROUP_M - pos.z);
      if (d < bd) { bd = d; best = g; }
    }
    const sig = this.dirty.get(best);
    this.dirty.delete(best);
    this._build(best, sig);
  }

  // the group's arrays + cell order on the pool (pool-forest.js forest.shell:
  // the tiles' arrays copied there, the result's moved back); the group's old mesh draws until the new one lands, a result
  // whose group went dirty again meanwhile is dropped (built again)
  _build(g, sig) {
    if (!sig) { this._drop(g); return; }
    this.stats.builds++;
    const parts = [];
    for (const [ij, d] of this.drawn) {
      if (this._gkey(d.i, d.j) !== g) continue;
      parts.push({ d, sides: this.skirtsOf.get(ij) || [] });
    }
    const job = this._job = { g, sig };
    const args = { parts: parts.map(({ d, sides }) => ({ sides, d: { x: d.x, y: d.y, z: d.z, flag: d.flag, index: d.index,
      y0: d.y0, cellM: d.cellM, i: d.i, j: d.j, tileM: d.tileM, ntTop: d.ntTop } })) };
    run('forest.shell', args).then((a) => {
      this._job = null;
      if (!this.dirty.has(g)) this._install(g, sig, a, parts.length);
    }).catch((e) => {
      if (this._job === job) this._job = null;
      console.error(`[forestvol] group ${g}: ${e.message}`);
    });
  }

  _drop(g) {
    const old = this.groups.get(g);
    if (old) { this.root.remove(old.mesh); old.mesh.geometry.dispose(); this.groups.delete(g); }
  }

  // a built group's mesh (forest.shell's arrays wrapped) replaces its old one.
  // The cell runs only while the main pass marches: the hit then lies on the
  // entry fragment's ray beyond it (a hidden entry hides the hit); steps 0
  // sinks the lid in the VS to the canopy tile's floor, below the box (the
  // whole group draws then)
  _install(g, sig, a, tiles) {
    this._drop(g);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(a.pos, 3));
    geo.setAttribute('aInfo', new THREE.BufferAttribute(a.info, 1));
    geo.setIndex(new THREE.BufferAttribute(a.idx, 1));
    // THE SEAT: the wall feet (flag 2), their sink (0 until the first re-read)
    let nf = 0;
    for (let v = 0; v < a.info.length; v++) if (a.info[v] % 4 === 2) nf++;
    const feet = new Uint32Array(nf);
    for (let v = 0, f = 0; v < a.info.length; v++) if (a.info[v] % 4 === 2) feet[f++] = v;
    geo.setAttribute('aSink', new THREE.BufferAttribute(new Float32Array(a.info.length), 1));
    if (a.cells) geo.userData.cells = { cells: seatCells(a.cells.cells), blocks: seatCells(a.cells.blocks), live: this._marching };
    geo.computeBoundingSphere();
    geo.boundingSphere.radius += TOP_PAD_M;   // the VS pad
    const m = new THREE.Mesh(geo, this.mat);
    m.name = `forestvol-${g}`;
    m.matrixAutoUpdate = false;
    m.onBeforeRender = this._before;
    this.root.add(m);
    m.updateMatrixWorld(true);
    this.groups.set(g, { mesh: m, sig, tris: a.ni / 3, skirts: a.ns, tiles, feet, seatGen: -1 });
  }

  // the main pass marches (its cell runs hold: viscull.js)
  _marching = () => (this.dbgSteps != null ? this.dbgSteps : this.steps.main) > 0;

  // per draw (each camera, each XR eye): the textures / indirection in sync,
  // the steps of this pass, its pixel angle
  _before = (renderer, scene, camera) => {
    this._sync();
    const w = this.opt.weapons, U = this.U;
    const optic = !!w && (camera === w.monoCam || camera === w.scopeCam);
    U.uSteps.value = this.dbgSteps != null ? this.dbgSteps : optic ? this.steps.optic : this.steps.main;
    const rt = renderer.getRenderTarget();
    const hpx = rt ? rt.height : renderer.getDrawingBufferSize(_v2).y;
    const eye = camera.isArrayCamera ? camera.cameras[0] : camera;   // multiview: ONE draw for both eyes (xrmv.js)
    U.uPixAng.value = 2 / (eye.projectionMatrix.elements[5] * Math.max(1, hpx));
    // THE DEPTH CUT: only inside the culled main pass (viscull.js pass: its
    // CDB holds for every eye of this pass), never an optic pass
    const cl = this.opt.cull, cdb = !optic && this.dbgCut && cl ? cl.pass : null;
    if (cdb && cl.passKey !== this._cdbKey) this._cdbUp(cdb, cl.passKey);
    U.uCdbOn.value = cdb ? 1 : 0;
    this.mat.uniformsNeedUpdate = true;
  };

  _cdbTex(w, h) {
    const t = new THREE.DataTexture(new Float32Array(w * h).fill(1e30), w, h, THREE.RedFormat, THREE.FloatType);
    t.minFilter = t.magFilter = THREE.NearestFilter;
    t.generateMipmaps = false;
    t.needsUpdate = true;
    return t;
  }

  // the key's zCover (+∞ → 1e30), its rotation and view rectangle
  _cdbUp(cdb, key) {
    const U = this.U, { cols, rows, z, M, rect } = cdb;
    let t = U.uCdb.value;
    if (t.image.width !== cols || t.image.height !== rows) { t.dispose(); t = U.uCdb.value = this._cdbTex(cols, rows); }
    const a = t.image.data;
    for (let k = 0; k < cols * rows; k++) a[k] = z[k] < 1e30 ? z[k] : 1e30;
    t.needsUpdate = true;
    U.uCdbM.value.set(M[0], M[1], M[2], M[3], M[4], M[5], M[6], M[7], M[8]);
    U.uCdbRect.value.set(rect[0], cdb.dX, rect[2], cdb.dY);
    U.uCdbN.value.set(cols, rows);
    this._cdbKey = key;
    this.stats.cdb++;
  }

  // the pool's textures (a reallocation changes their identity) + the
  // indirection when the resident set moved
  _sync() {
    const tx = canopyTextures(), U = this.U, A = tx ? tx.A : this.ph.A;
    if (U.uA.value !== A) { U.uA.value = A; U.uB.value = tx ? tx.B : this.ph.B; U.uH.value = tx ? tx.H : this.ph.H; }
    const P = gen.pools.get('canopy');
    const key = `${gen.stats.done}:${gen.stats.evicted}:${P ? P.layers : 0}:${A.uuid}`;
    if (key === this._indKey) return;
    this._indKey = key;
    const [nx, nz] = this.indN, a = this.ind.image.data;
    a.fill(0);
    // coarse first: a finer tile overwrites its cells
    for (const t of canopyTiles().sort((p, q) => q.c - p.c)) {
      const n = 2 ** t.c, ox = Math.round(t.X0 / IND_M), oz = Math.round(t.Z0 / IND_M);
      for (let b = 0; b < n; b++) {
        const z = oz + b;
        if (z < 0 || z >= nz) continue;
        for (let c = 0; c < n; c++) {
          const x = ox + c;
          if (x < 0 || x >= nx) continue;
          const k = 4 * (z * nx + x);
          a[k] = t.layer; a[k + 1] = t.c; a[k + 2] = t.y0; a[k + 3] = 1;
        }
      }
    }
    this.ind.needsUpdate = true;
    this.stats.ind++;
  }

  state() {
    let tris = 0, skirts = 0;
    for (const g of this.groups.values()) { tris += g.tris; skirts += g.skirts; }
    return { groups: this.groups.size, tiles: this.drawn.size, tris, skirts, draws: this.plan ? this.plan.draws : 0,
      planned: this.plan ? this.plan.tiles.length : 0, dirty: this.dirty.size, fetched: this.stats.fetched,
      failed: this.stats.failed, cdb: this.stats.cdb, cut: this.dbgCut, seated: this.stats.seated, seat_ms: +this.stats.seat_ms.toFixed(1), seat: this.U.uSeat.value > 0, bad: this.bad.size, lru: this.lru.size, pending: this.pend.size,
      steps: { ...this.steps }, cls: this.cls, depth: depthMode(this.cls), ind: this.stats.ind };
  }
}

// THE SEAT: a cell / block box reaches SEAT_MAX_M lower (a wall foot the VS
// sank under the drawn ground draws inside its cell's box: viscull P3)
function seatCells(list) {
  for (const c of list) c.lo = [c.lo[0], c.lo[1] - SEAT_MAX_M, c.lo[2]];
  return list;
}

// ---------------------------------------------------------------- module API
let V = null;
let GROUND = null;   // THE SEAT's drawn ground: the TerrainManager (groundDrawn, gen) — trees.js Forest hands it over

// the drawn ground the wall feet seat on (trees.js: the ground its sprites stand on)
export function forestVolGround(terrain) { GROUND = terrain && typeof terrain.groundDrawn === 'function' ? terrain : null; }

// main.js, after installCanopy: nothing without meta.forest.shell; a shell
// that is not FSH1 throws (no compat)
export function installForestVol(opt) {
  if (V || !opt.meta || !opt.meta.forest || !opt.meta.forest.shell) return null;
  V = new ForestVolume(opt);
  // the dev probe hook: debug view, forced steps (null = the tier's), on / off
  globalThis.__fv = {
    debug: (m) => { V.U.uDbg.value = m | 0; },
    steps: (n) => { V.dbgSteps = n == null ? null : n | 0; },
    cut: (on) => { V.dbgCut = on !== false; },
    show: (on) => { V.root.visible = !!on; },
    seat: (on) => { V.U.uSeat.value = on === false ? 0 : 1; },
    dith: (m) => { V.U.uDith.value = m | 0; },
    // read back the H layer the VOLUME binds (its own GL object), + the resident tiles
    peek: (layer, lvl = 0) => {
      const r = V.opt.renderer, gl = r.getContext(), tp = r.properties.get(V.U.uH.value), tex = tp.__webglTexture;
      const tx = canopyTextures(), n = CANOPY_N >> lvl, px = new Uint32Array(n * n * 4);
      const fb = gl.createFramebuffer();
      gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
      gl.framebufferTextureLayer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, tex, lvl, layer);
      const st = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
      if (st === gl.FRAMEBUFFER_COMPLETE) gl.readPixels(0, 0, n, n, gl.RGBA_INTEGER, gl.UNSIGNED_INT, px);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null); gl.deleteFramebuffer(fb); r.resetState();
      let nz = 0, mx = 0;
      for (let k = 0; k < n * n; k++) { const v = px[4 * k]; if (v) { nz++; if (v > mx) mx = v; } }
      return { st, complete: st === gl.FRAMEBUFFER_COMPLETE, nz, mx, same: !!tx && V.U.uH.value === tx.H, ver: V.U.uH.value.version, glver: tp.__version,
        tiles: canopyTiles().map((t) => [t.layer, t.c, t.i, t.j, Math.round(t.y0)]).slice(0, 40) };
    },
  };
  return V;
}
// once a tick (main.js, next to canopyTick, before genFrame): the eye
export function forestVolTick(pos) { if (V) V.tick(pos); }
// THE SPLATS (splatforest.js): { reach (m), tex (uSplatCov), holds(gx, gz, groupM) } | null
export function setForestVolSplat(sp) { if (V) { V.splat = sp; V._splatVis(); } }
// THE SPLATS draw only where the volume does (an exact replacement, never a fill of its loading holes):
// each 1024 m shell tile drawn AND in an installed group mesh → cb(i, j)
export function forestVolResident(cb) {
  if (!V) return;
  for (const d of V.drawn.values()) if (V.groups.has(V._gkey(d.i, d.j))) cb(d.i, d.j);
}
// the uniforms the splats shade with: the volume's own objects (THE look by construction)
export function forestVolSplatLook() {
  if (!V) return null;
  const U = V.U;
  return {
    uniforms: { uInd: U.uInd, uIndOrigin: U.uIndOrigin, uIndN: U.uIndN, uA: U.uA, uB: U.uB, uH: U.uH, uSky: U.uSky, uSun: U.uSun, uL: U.uL,
      uWhite: U.uWhite, uGain: U.uGain, uSat: U.uSat, uSnowGain: U.uSnowGain, uSeason: U.uSeason, uRange: U.uRange },
    sync: () => V._sync(),
  };
}
// the dev hook (__vr.gen().forestvol)
export function forestVolState() { return V ? V.state() : null; }

// the volume (null without one) — main.js's world-swap hook (worldswap.js forestHook)
export function forestVolume() { return V; }
// the drawn groups ('gx_gz' → {mesh, …}) — viscull.js's volume units
export function forestVolGroups() { return V ? V.groups : null; }
// the near sprites' canopy lookup (trees.js): the volume's own uniform
// objects (indirection, B, light, season, snow) + sync() — the per-draw
// textures / indirection refresh (call it in the sprite's onBeforeRender);
// null without a volume (the sprites then draw unlit by it, to N)
export function forestVolLookup() {
  if (!V) return null;
  const U = V.U;
  return {
    uniforms: { uInd: U.uInd, uIndOrigin: U.uIndOrigin, uIndN: U.uIndN, uB: U.uB, uSky: U.uSky, uSun: U.uSun, uL: U.uL,
      uWhite: U.uWhite, uSnowGain: U.uSnowGain, uSeason: U.uSeason },
    sync: () => V._sync(),
  };
}
